import React, { forwardRef, useRef, useState, useCallback, useEffect, useContext } from 'react';
import warning from 'warning';
import { useId } from '@reach/auto-id';
import { useForkedRef, wrapEvent, getOwnerDocument, checkStyles, isFunction, makeId, useIsomorphicLayoutEffect, createNamedContext } from '@reach/utils';
function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var SLIDER_ORIENTATION_HORIZONTAL = "horizontal";
var SLIDER_ORIENTATION_VERTICAL = "vertical"; // Handle is centered directly over the current value marker

var SLIDER_HANDLE_ALIGN_CENTER = "center"; // Handle is contained within the bounds of the track, offset slightly from the
// value's center mark to accommodate

var SLIDER_HANDLE_ALIGN_CONTAIN = "contain";
var SliderContext = /*#__PURE__*/createNamedContext("SliderContext", {});
var useSliderContext = function useSliderContext() {
  return useContext(SliderContext);
}; // These proptypes are shared between the composed SliderInput component and the
// simplified Slider
////////////////////////////////////////////////////////////////////////////////
/**
 * Slider
 *
 * @see Docs https://reacttraining.com/reach-ui/slider#slider
 */
var Slider = /*#__PURE__*/forwardRef(function Slider(_ref, forwardedRef) {
  var children = _ref.children,
    props = _objectWithoutPropertiesLoose(_ref, ["children"]);
  return React.createElement(SliderInput, Object.assign({
    ref: forwardedRef
  }, props), React.createElement(SliderTrack, null, React.createElement(SliderTrackHighlight, null), React.createElement(SliderHandle, null), children));
});
if (process.env.NODE_ENV !== "production") {
  Slider.displayName = "Slider";
}

/**
 * SliderInput
 *
 * The parent component of the slider interface. This is a lower level component
 * if you need more control over styles or rendering the slider's inner
 * components.
 *
 * @see Docs https://reacttraining.com/reach-ui/slider#sliderinput
 */

var SliderInput = /*#__PURE__*/forwardRef(function SliderInput(_ref2, forwardedRef) {
  var ariaLabelledBy = _ref2["aria-labelledby"],
    ariaValueText = _ref2["aria-valuetext"],
    defaultValue = _ref2.defaultValue,
    _ref2$disabled = _ref2.disabled,
    disabled = _ref2$disabled === void 0 ? false : _ref2$disabled,
    controlledValue = _ref2.value,
    getValueText = _ref2.getValueText,
    _ref2$handleAlignment = _ref2.handleAlignment,
    handleAlignment = _ref2$handleAlignment === void 0 ? SLIDER_HANDLE_ALIGN_CENTER : _ref2$handleAlignment,
    _ref2$max = _ref2.max,
    max = _ref2$max === void 0 ? 100 : _ref2$max,
    _ref2$min = _ref2.min,
    min = _ref2$min === void 0 ? 0 : _ref2$min,
    name = _ref2.name,
    onChange = _ref2.onChange,
    onKeyDown = _ref2.onKeyDown,
    onPointerDown = _ref2.onPointerDown,
    onPointerMove = _ref2.onPointerMove,
    onPointerUp = _ref2.onPointerUp,
    _ref2$orientation = _ref2.orientation,
    orientation = _ref2$orientation === void 0 ? SLIDER_ORIENTATION_HORIZONTAL : _ref2$orientation,
    stepProp = _ref2.step,
    children = _ref2.children,
    rest = _objectWithoutPropertiesLoose(_ref2, ["aria-label", "aria-labelledby", "aria-valuetext", "defaultValue", "disabled", "value", "getValueText", "handleAlignment", "max", "min", "name", "onChange", "onKeyDown", "onPointerDown", "onPointerMove", "onPointerUp", "orientation", "step", "children"]);

  // Verify that the component is either controlled or uncontrolled throughout
  // its lifecycle
  var _useRef = useRef(controlledValue != null),
    isControlled = _useRef.current;
  process.env.NODE_ENV !== "production" ? warning(!(isControlled && controlledValue == null), "Slider is changing from controlled to uncontrolled. Slider should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled Slider for the lifetime of the component. Check the `value` prop being passed in.") : void 0;
  process.env.NODE_ENV !== "production" ? warning(!(!isControlled && controlledValue != null), "Slider is changing from uncontrolled to controlled. Slider should not switch from uncontrolled to controlled (or vice versa). Decide between using a controlled or uncontrolled Slider for the lifetime of the component. Check the `value` prop being passed in.") : void 0;
  var id = useId(rest.id);
  var trackRef = useRef(null);
  var handleRef = useRef(null);
  var sliderRef = useRef(null);
  var ref = useForkedRef(sliderRef, forwardedRef);
  var _useState = useState(false),
    hasFocus = _useState[0],
    setHasFocus = _useState[1];
  var _useState2 = useState(false),
    isPointerDown = _useState2[0],
    setPointerDown = _useState2[1];
  var _useState3 = useState(defaultValue || min),
    internalValue = _useState3[0],
    setValue = _useState3[1];
  var _useDimensions = useDimensions(handleRef),
    handleDimensions = _objectWithoutPropertiesLoose(_useDimensions, ["ref"]);
  var _value = isControlled ? controlledValue : internalValue;
  var value = getAllowedValue(_value, min, max);
  var trackPercent = valueToPercent(value, min, max);
  var isVertical = orientation === SLIDER_ORIENTATION_VERTICAL;
  var step = stepProp || 1;
  var handleSize = isVertical ? handleDimensions.height : handleDimensions.width;
  var handlePosition = "calc(" + trackPercent + "% - " + (handleAlignment === SLIDER_HANDLE_ALIGN_CENTER ? handleSize + "px / 2" : handleSize + "px * " + trackPercent * 0.01) + ")";
  var updateValue = useCallback(function updateValue(newValue) {
    if (!isControlled) {
      setValue(newValue);
    }
    if (onChange) {
      onChange(newValue, {
        min: min,
        max: max,
        handlePosition: handlePosition
      });
    }
  }, [handlePosition, isControlled, max, min, onChange]);
  var getNewValueFromPointer = useCallback(function (event) {
    if (trackRef.current) {
      var _trackRef$current$get = trackRef.current.getBoundingClientRect(),
        left = _trackRef$current$get.left,
        width = _trackRef$current$get.width,
        bottom = _trackRef$current$get.bottom,
        height = _trackRef$current$get.height;
      var clientX = event.clientX,
        clientY = event.clientY;
      var diff = isVertical ? bottom - clientY : clientX - left;
      var percent = diff / (isVertical ? height : width);
      var newValue = percentToValue(percent, min, max);
      if (step) {
        newValue = roundValueToStep(newValue, step);
      }
      newValue = getAllowedValue(newValue, min, max);
      return newValue;
    }
    return null;
  }, [isVertical, max, min, step]);
  var handleKeyDown = wrapEvent(onKeyDown, function (event) {
    var flag = false;
    var newValue;
    var tenSteps = (max - min) / 10;
    var keyStep = stepProp || (max - min) / 100;
    switch (event.key) {
      case "ArrowLeft":
      case "ArrowDown":
        newValue = value - keyStep;
        flag = true;
        break;
      case "ArrowRight":
      case "ArrowUp":
        newValue = value + keyStep;
        flag = true;
        break;
      case "PageDown":
        newValue = value - tenSteps;
        flag = true;
        break;
      case "PageUp":
        newValue = value + tenSteps;
        flag = true;
        break;
      case "Home":
        newValue = min;
        flag = true;
        break;
      case "End":
        newValue = max;
        flag = true;
        break;
      default:
        return;
    }
    if (flag) {
      event.preventDefault();
      newValue = roundValueToStep(newValue, keyStep);
      newValue = getAllowedValue(newValue, min, max);
      updateValue(newValue);
    }
  });
  var handlePointerDown = wrapEvent(onPointerDown, function (event) {
    event.preventDefault();
    if (disabled) {
      if (isPointerDown) setPointerDown(false);
      return;
    }
    if (sliderRef.current && handleRef.current) {
      setPointerDown(true);
      var newValue = getNewValueFromPointer(event);
      sliderRef.current.setPointerCapture && sliderRef.current.setPointerCapture(event.pointerId);
      if (newValue != null && newValue !== value) {
        updateValue(newValue);
      }
      handleRef.current.focus();
    }
  });
  var handlePointerUp = wrapEvent(onPointerUp, function (event) {
    if (sliderRef.current && event.pointerId) {
      sliderRef.current.releasePointerCapture && sliderRef.current.releasePointerCapture(event.pointerId);
    }
    setPointerDown(false);
  });
  var valueText = getValueText ? getValueText(value) : ariaValueText;
  var trackHighlightStyle = isVertical ? {
    width: "100%",
    height: trackPercent + "%",
    bottom: 0
  } : {
    width: trackPercent + "%",
    height: "100%",
    left: 0
  };
  var ctx = {
    ariaLabelledBy: ariaLabelledBy,
    handleDimensions: handleDimensions,
    handlePosition: handlePosition,
    handleRef: handleRef,
    hasFocus: hasFocus,
    onKeyDown: onKeyDown,
    onPointerDown: onPointerDown,
    onPointerMove: onPointerMove,
    onPointerUp: onPointerUp,
    onHandleKeyDown: handleKeyDown,
    setHasFocus: setHasFocus,
    sliderId: id,
    sliderMax: max,
    sliderMin: min,
    value: value,
    valueText: valueText,
    disabled: !!disabled,
    isVertical: isVertical,
    orientation: orientation,
    sliderStep: step,
    trackPercent: trackPercent,
    trackRef: trackRef,
    trackHighlightStyle: trackHighlightStyle,
    updateValue: updateValue
  };
  var dataAttributes = makeDataAttributes("slider", {
    disabled: disabled,
    orientation: orientation
  });
  useEffect(function () {
    var ownerDocument = getOwnerDocument(sliderRef.current) || document;
    var handlePointerMove = wrapEvent(onPointerMove, function (event) {
      var newValue = getNewValueFromPointer(event);
      if (newValue !== value) {
        updateValue(newValue);
      }
    });
    if (isPointerDown) {
      ownerDocument.addEventListener("pointermove", handlePointerMove);
    }
    return function () {
      ownerDocument.removeEventListener("pointermove", handlePointerMove);
    };
  }, [getNewValueFromPointer, isPointerDown, onPointerMove, updateValue, value]);
  useEffect(function () {
    return checkStyles("slider");
  }, []);
  return React.createElement(SliderContext.Provider, {
    value: ctx
  }, React.createElement("div", Object.assign({
    "aria-disabled": disabled
  }, rest, dataAttributes, {
    ref: ref,
    tabIndex: -1,
    onPointerDown: handlePointerDown,
    onPointerUp: handlePointerUp
  }), isFunction(children) ? children({
    hasFocus: hasFocus,
    id: id,
    max: max,
    min: min,
    value: value,
    valueText: valueText
  }) : children, name &&
  // If the slider is used in a form we'll need an input field to
  // capture the value. We'll assume this when the component is given a
  // form field name (A `name` prop doesn't really make sense in any
  // other context).
  React.createElement("input", {
    type: "hidden",
    value: value,
    name: name,
    id: id && makeId("input", id)
  })));
});
if (process.env.NODE_ENV !== "production") {
  SliderInput.displayName = "SliderInput";
} ////////////////////////////////////////////////////////////////////////////////

/**
 * SliderTrack
 *
 * @see Docs https://reacttraining.com/reach-ui/slider#slidertrack
 */

var SliderTrack = /*#__PURE__*/forwardRef(function SliderTrack(_ref3, forwardedRef) {
  var children = _ref3.children,
    _ref3$style = _ref3.style,
    style = _ref3$style === void 0 ? {} : _ref3$style,
    props = _objectWithoutPropertiesLoose(_ref3, ["children", "style"]);
  var _useSliderContext = useSliderContext(),
    disabled = _useSliderContext.disabled,
    orientation = _useSliderContext.orientation,
    trackRef = _useSliderContext.trackRef;
  var ref = useForkedRef(trackRef, forwardedRef);
  var dataAttributes = makeDataAttributes("slider-track", {
    orientation: orientation,
    disabled: disabled
  });
  return React.createElement("div", Object.assign({
    ref: ref,
    style: _extends({}, style, {
      position: "relative"
    })
  }, dataAttributes, props), children);
});
if (process.env.NODE_ENV !== "production") {
  SliderTrack.displayName = "SliderTrack";
} ////////////////////////////////////////////////////////////////////////////////

/**
 * SliderTrackHighlight
 *
 * The (typically) highlighted portion of the track that represents the space
 * between the slider's `min` value and its current value.
 *
 * @see Docs https://reacttraining.com/reach-ui/slider#slidertrackhighlight
 */

var SliderTrackHighlight = /*#__PURE__*/forwardRef(function SliderTrackHighlight(_ref4, forwardedRef) {
  var _ref4$style = _ref4.style,
    style = _ref4$style === void 0 ? {} : _ref4$style,
    props = _objectWithoutPropertiesLoose(_ref4, ["children", "style"]);
  var _useSliderContext2 = useSliderContext(),
    disabled = _useSliderContext2.disabled,
    orientation = _useSliderContext2.orientation,
    trackHighlightStyle = _useSliderContext2.trackHighlightStyle;
  var dataAttributes = makeDataAttributes("slider-track-highlight", {
    orientation: orientation,
    disabled: disabled
  });
  return React.createElement("div", Object.assign({
    ref: forwardedRef,
    style: _extends({
      position: "absolute"
    }, trackHighlightStyle, {}, style)
  }, dataAttributes, props));
});
if (process.env.NODE_ENV !== "production") {
  SliderTrackHighlight.displayName = "SliderTrackHighlight";
} ////////////////////////////////////////////////////////////////////////////////

/**
 * SliderHandle
 *
 * The handle that the user drags along the track to set the slider value.
 *
 * @see Docs https://reacttraining.com/reach-ui/slider#sliderhandle
 */

var SliderHandle = /*#__PURE__*/forwardRef(function SliderHandle(_ref5, forwardedRef) {
  var onBlur = _ref5.onBlur,
    onFocus = _ref5.onFocus,
    _ref5$style = _ref5.style,
    style = _ref5$style === void 0 ? {} : _ref5$style,
    onKeyDown = _ref5.onKeyDown,
    _ref5$tabIndex = _ref5.tabIndex,
    tabIndex = _ref5$tabIndex === void 0 ? 0 : _ref5$tabIndex,
    props = _objectWithoutPropertiesLoose(_ref5, ["onBlur", "onFocus", "style", "onKeyDown", "tabIndex"]);
  var _useSliderContext3 = useSliderContext(),
    ariaLabelledBy = _useSliderContext3.ariaLabelledBy,
    disabled = _useSliderContext3.disabled,
    handlePosition = _useSliderContext3.handlePosition,
    handleRef = _useSliderContext3.handleRef,
    isVertical = _useSliderContext3.isVertical,
    onHandleKeyDown = _useSliderContext3.onHandleKeyDown,
    orientation = _useSliderContext3.orientation,
    setHasFocus = _useSliderContext3.setHasFocus,
    sliderMin = _useSliderContext3.sliderMin,
    sliderMax = _useSliderContext3.sliderMax,
    value = _useSliderContext3.value,
    valueText = _useSliderContext3.valueText;
  var ref = useForkedRef(handleRef, forwardedRef);
  var dataAttributes = makeDataAttributes("slider-handle", {
    orientation: orientation,
    disabled: disabled
  });
  return React.createElement("div", Object.assign({
    role: "slider",
    "aria-disabled": disabled,
    "aria-valuemin": sliderMin,
    "aria-valuetext": valueText,
    "aria-orientation": orientation,
    "aria-valuenow": value,
    "aria-valuemax": sliderMax,
    "aria-labelledby": ariaLabelledBy
  }, props, dataAttributes, {
    ref: ref,
    onBlur: wrapEvent(onBlur, function () {
      setHasFocus(false);
    }),
    onFocus: wrapEvent(onFocus, function () {
      setHasFocus(true);
    }),
    onKeyDown: wrapEvent(onKeyDown, onHandleKeyDown),
    style: _extends({
      position: "absolute"
    }, isVertical ? {
      bottom: handlePosition
    } : {
      left: handlePosition
    }, {}, style),
    tabIndex: disabled ? undefined : tabIndex
  }));
});
if (process.env.NODE_ENV !== "production") {
  SliderHandle.displayName = "SliderHandle";
} ////////////////////////////////////////////////////////////////////////////////

/**
 * SliderMarker
 *
 * A fixed value marker. These can be used to illustrate a range of steps or
 * highlight important points along the slider track.
 *
 * @see Docs https://reacttraining.com/reach-ui/slider#slidermarker
 */

var SliderMarker = /*#__PURE__*/forwardRef(function SliderMarker(_ref6, forwardedRef) {
  var children = _ref6.children,
    _ref6$style = _ref6.style,
    style = _ref6$style === void 0 ? {} : _ref6$style,
    value = _ref6.value,
    props = _objectWithoutPropertiesLoose(_ref6, ["children", "style", "value"]);
  var _useSliderContext4 = useSliderContext(),
    disabled = _useSliderContext4.disabled,
    isVertical = _useSliderContext4.isVertical,
    orientation = _useSliderContext4.orientation,
    sliderMin = _useSliderContext4.sliderMin,
    sliderMax = _useSliderContext4.sliderMax,
    sliderValue = _useSliderContext4.value;
  var inRange = !(value < sliderMin || value > sliderMax);
  var highlight = sliderValue >= value;
  var dataAttributes = makeDataAttributes("slider-marker", {
    orientation: orientation,
    disabled: disabled,
    highlight: highlight
  });
  var absoluteStartPosition = valueToPercent(value, sliderMin, sliderMax) + "%";
  return inRange ? React.createElement("div", Object.assign({
    ref: forwardedRef,
    style: _extends({
      position: "absolute"
    }, isVertical ? {
      bottom: absoluteStartPosition
    } : {
      left: absoluteStartPosition
    }, {}, style)
  }, dataAttributes, props, {
    children: children
  })) : null;
});
if (process.env.NODE_ENV !== "production") {
  SliderMarker.displayName = "SliderMarker";
} ////////////////////////////////////////////////////////////////////////////////

function getAllowedValue(val, min, max) {
  return val > max ? max : val < min ? min : val;
}
function makeDataAttributes(component, _ref7) {
  var _ref8;
  if (component === void 0) {
    component = "slider";
  }
  var orientation = _ref7.orientation,
    highlight = _ref7.highlight,
    disabled = _ref7.disabled;
  return _ref8 = {}, _ref8["data-reach-" + component] = "", _ref8["data-reach-" + component + "-disabled"] = disabled ? "" : undefined, _ref8["data-reach-" + component + "-orientation"] = orientation, _ref8["data-reach-" + component + "-highlight"] = highlight ? orientation : undefined, _ref8;
}
function makeValuePrecise(value, step) {
  var stepDecimalPart = step.toString().split(".")[1];
  var stepPrecision = stepDecimalPart ? stepDecimalPart.length : 0;
  return Number(value.toFixed(stepPrecision));
}
function percentToValue(percent, min, max) {
  return (max - min) * percent + min;
}
function roundValueToStep(value, step) {
  return makeValuePrecise(Math.round(value / step) * step, step);
}
function useDimensions(ref) {
  var _useState4 = useState({
      width: 0,
      height: 0
    }),
    _useState4$ = _useState4[0],
    width = _useState4$.width,
    height = _useState4$.height,
    setDimensions = _useState4[1]; // Many existing `useDimensions` type hooks will use `getBoundingClientRect`
  // getBoundingClientRect does not work here when borders are applied.
  // getComputedStyle is not as performant so we may want to create a utility to
  // check for any conflicts with box sizing first and only use
  // `getComputedStyle` if neccessary.

  /* const { width, height } = ref.current
    ? ref.current.getBoundingClientRect()
    : 0; */

  useIsomorphicLayoutEffect(function () {
    if (ref.current) {
      var _window$getComputedSt = window.getComputedStyle(ref.current),
        _newHeight = _window$getComputedSt.height,
        _newWidth = _window$getComputedSt.width;
      var newHeight = parseFloat(_newHeight);
      var newWidth = parseFloat(_newWidth);
      if (newHeight !== height || newWidth !== width) {
        setDimensions({
          height: newHeight,
          width: newWidth
        });
      }
    }
  }, [ref, width, height]);
  return {
    ref: ref,
    width: width,
    height: height
  };
}
function valueToPercent(value, min, max) {
  return (value - min) * 100 / (max - min);
}
export default Slider;
export { SLIDER_HANDLE_ALIGN_CENTER, SLIDER_HANDLE_ALIGN_CONTAIN, SLIDER_ORIENTATION_HORIZONTAL, SLIDER_ORIENTATION_VERTICAL, Slider, SliderHandle, SliderInput, SliderMarker, SliderTrack, SliderTrackHighlight };